import { useEffect, useMemo, type JSX } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { BRAND, FEATURES } from '@yuan-math/config';
import { metaForPath } from '@yuan-math/seo';
import { registry, useLearner } from '../state/LearnerContext.tsx';
import { freezesHeld, levelForXp, streakStatus } from '@yuan-math/learning';
import type { SyncState } from '../state/LearnerContext.tsx';
import { useAuth } from '../state/AuthContext.tsx';
import { RewardsProvider } from '../state/RewardsContext.tsx';
import { Seo } from './Seo.tsx';

/**
 * The application shell.
 *
 * Navigation changes with who is looking (§18): a visitor sees the marketing
 * routes, a signed-in student sees their own. The density attribute
 * implements the age-appropriate interface principle without forking the UI.
 *
 * The chrome carries exactly two numbers — the streak and the level — because
 * they are what a returning student looks for before anything else. They are
 * habit, never a claim about mathematical ability (§10): nothing unlocks on
 * them, and every page under this bar leads with mastery instead.
 */

/**
 * Where the app can go, in one list, used by the header and the thumb rail.
 *
 * Progress and Badges are deliberately absent: the streak and level chips in
 * the header already lead to those two pages, and they are rendered under
 * exactly the same condition this list is (`signedIn` *is* "there is a
 * profile"), on every width — the chips stay in the bar when the links move to
 * the rail. Listing them twice gave the same destination two names a few
 * pixels apart, which reads as two places rather than one.
 */
const LEARNER_NAV = [
  { to: '/home', label: 'Learn', icon: '🏠' },
  { to: '/courses', label: 'Courses', icon: '📚' },
  { to: '/map', label: 'Map', icon: '🗺️' },
  /*
   * The adventure, as one door rather than two.
   *
   * `/adventure` is the hub the whole feature is arranged around
   * (docs/rpg.md §34–35): the world map, the character, the journal and the
   * kingdom the student is standing in are all one press from it, and the
   * Continue Adventure button on it is the single most important control the
   * RPG has. A second chrome entry pointing straight at `/adventure/map` would
   * sit next to "Map" — which already means the Math Map — and offer the same
   * feature two names a few pixels apart, which is the mistake the note above
   * describes. `NavLink` has no `end`, so this stays lit for every
   * `/adventure/*` page.
   */
  { to: '/adventure', label: 'Adventure', icon: '⚔️' },
];

const VISITOR_NAV = [
  { to: '/courses', label: 'Learn', icon: '📚' },
  { to: '/map', label: 'Map', icon: '🗺️' },
  // No door to a page that is not there: `/pricing` is only routed when
  // `FEATURES.payments` is on, the same rule the adventure flag follows.
  ...(FEATURES.payments ? [{ to: '/pricing', label: 'Pricing', icon: '✨' }] : []),
  { to: '/about', label: 'About', icon: '💬' },
];

/**
 * A quiet indicator that progress is reaching the server.
 *
 * Deliberately small and unlabelled until something is wrong: a student should
 * not have to think about syncing, but they should be able to find out.
 */
function SyncDot({ state }: { state: SyncState }): JSX.Element | null {
  if (state === 'off') return null;
  const label =
    state === 'syncing' ? 'Saving your progress…' : state === 'error' ? 'Not saved to your account yet' : 'Progress saved';
  return <span className={`sync-dot sync-dot--${state}`} role="status" aria-label={label} title={label} />;
}

export function Layout(): JSX.Element {
  const { profile, signedIn, signOut, syncState } = useLearner();
  const { signedIn: hasAccount, configured: accountsAvailable, isAdmin, ready: accountKnown } = useAuth();
  const location = useLocation();

  // Density follows the learner's age band; the curriculum is unchanged (§ Principle 5).
  useEffect(() => {
    const density =
      profile?.ageBand === 'kindergarten' || profile?.ageBand === 'elementary'
        ? 'young'
        : profile?.ageBand === 'university' || profile?.ageBand === 'adult'
          ? 'compact'
          : 'default';
    document.documentElement.setAttribute('data-density', density);
  }, [profile?.ageBand]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  /**
   * What this URL tells a search engine.
   *
   * Resolved from the path rather than supplied by each page, for two reasons:
   * a page cannot forget, and it is `metaForPath` that `scripts/prerender.ts`
   * calls when it writes the static copy of this same URL. The document a
   * crawler fetches and the document a student navigates to are therefore
   * described identically, by construction rather than by discipline.
   */
  const meta = useMemo(() => metaForPath(registry, location.pathname), [location.pathname]);

  const level = profile ? levelForXp(profile.xp) : null;
  const streak = profile?.streak;
  const status = streak ? streakStatus(streak, Date.now()) : 'none';
  const rest = streak ? freezesHeld(streak) : 0;
  /*
   * The adventure is offered to a learner and only when it is switched on
   * (`FEATURES.rpg`). A visitor is not shown it because every page under it
   * needs a profile; with the flag off the routes are a 404, and a nav item
   * leading to one is worse than no nav item at all.
   */
  const learnerNav = FEATURES.rpg ? LEARNER_NAV : LEARNER_NAV.filter((item) => item.to !== '/adventure');
  const nav = signedIn ? learnerNav : VISITOR_NAV;

  /*
   * Two questions, and this bar used to ask only one of them.
   *
   * `signedIn` from the learner context means "there is a profile on this
   * device"; `hasAccount` means "there is a session". They are independent — a
   * guest has the first without the second, and an account whose profile has
   * not arrived has the second without the first. Branching every control on
   * the learner therefore offered "Sign in" to somebody who had just signed in.
   * Each control below now answers the question it is actually about.
   */
  const showAccountControls = accountsAvailable && hasAccount;

  /*
   * ...and neither question is answered instantly.
   *
   * Restoring a session is a network round trip, so for the first moments of
   * every page load `hasAccount` is false because nobody has looked yet — not
   * because nobody is there. Rendering the visitor controls on that means a
   * signed-in student is offered "Create an account", and following it lands on
   * a sign-up form for the account they already have. An empty slot for a
   * fraction of a second is the honest version; the answer never flips twice.
   */
  const showGuestControls = accountKnown && !hasAccount;

  /**
   * Ends a guest, after asking.
   *
   * A signed-out learner's XP, streak and every mastery measurement live in
   * this browser and nowhere else, so the same click that merely closes a
   * session for an account holder destroys months of work for a guest — with
   * nothing to restore it from. The button names the consequence and this
   * confirms it, which is the treatment `AccountPage` already gives the only
   * other irreversible control on the site.
   */
  const clearDevice = (): void => {
    const whose = profile ? `${profile.displayName}'s progress` : 'this learner';
    if (!window.confirm(`Clear ${whose} from this device? There is no account holding it, so this cannot be undone.`)) {
      return;
    }
    void signOut();
  };

  return (
    <div className="shell">
      <Seo meta={meta} />
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <header className="site-header">
        <div className="site-header__inner">
          <Link to={signedIn ? '/home' : '/'} className="wordmark">
            <span className="wordmark__mark" aria-hidden="true">
              ∑
            </span>
            <span className="wordmark__text">{BRAND.name}</span>
          </Link>

          <nav className="site-nav" aria-label="Main">
            {nav.map((item) => (
              <NavLink key={item.to} to={item.to}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="site-header__actions">
            {profile ? (
              <>
                {/*
                  These two chips are now the only way to Progress and Badges,
                  so each one names where it goes as well as what it counts. A
                  number alone was a fine ornament beside a "Progress" link; on
                  its own it is a control whose destination has to be guessed.
                */}
                <Link
                  to="/progress"
                  className={`statchip statchip--streak statchip--${status}`}
                  title={`${streakTitle(profile.streak.current, status, rest)} · open Progress`}
                  aria-label={`${streakTitle(profile.streak.current, status, rest)} · open Progress`}
                >
                  <span className="statchip__icon" aria-hidden="true">
                    🔥
                  </span>
                  <span aria-hidden="true">{profile.streak.current}</span>
                  {/* Rest days are shown where the streak is shown, so a
                      student knows the cover exists before they need it. */}
                  {rest > 0 ? (
                    <span className="statchip__rest" aria-hidden="true">
                      {'❄️'.repeat(rest)}
                    </span>
                  ) : null}
                </Link>
                <Link
                  to="/achievements"
                  className="statchip statchip--xp"
                  title={`Level ${level?.level} · ${profile.xp} XP · open Badges`}
                  aria-label={`Level ${level?.level}, ${profile.xp} XP · open Badges`}
                >
                  <span className="statchip__icon" aria-hidden="true">
                    ⚡
                  </span>
                  <span aria-hidden="true">{level?.level}</span>
                </Link>
              </>
            ) : null}

            {/* Shown only to the handful of people it belongs to. Hiding it
                from everybody else is tidiness, not security — the database
                refuses the queries either way. */}
            {isAdmin ? (
              <Link to="/admin" className="btn btn--ghost btn--sm">
                Admin
              </Link>
            ) : null}

            {showAccountControls ? (
              <Link to="/account" className="btn btn--ghost btn--sm">
                <SyncDot state={syncState} />
                Account
              </Link>
            ) : accountsAvailable && showGuestControls && profile ? (
              /*
               * It says what it opens, because it opened something else.
               *
               * This used to read "Save my progress", and the page behind it is
               * headed "Create an account" — so the button promised an action
               * and delivered a form. Worse, it sits beside a control that used
               * to say "Sign out" (below), and the pair reads as a site that
               * has forgotten the account you just made. The reason to have one
               * is a sentence, and `/register` already leads with it; a button
               * this size can only carry a destination.
               */
              <Link to="/register" className="btn btn--secondary btn--sm">
                Create an account
              </Link>
            ) : null}

            {/*
              Two buttons, because there are two things to end.
              `signOut` does both halves — it closes the session *and* clears
              this device — but a guest has no session, so labelling their
              button "Sign out" claimed a sign-in that never happened. Naming
              what it actually does is also the honest warning: for a learner
              with no account, this progress exists nowhere else.

              Only the guest is asked to confirm. Signing a real account out is
              a keystroke away from being undone; the other button is the one
              irreversible control in this bar.
            */}
            {hasAccount ? (
              <button type="button" className="btn btn--ghost btn--sm" onClick={signOut}>
                Sign out
              </button>
            ) : showGuestControls && profile ? (
              <button type="button" className="btn btn--ghost btn--sm" onClick={clearDevice}>
                Clear this device
              </button>
            ) : showGuestControls ? (
              <>
                {accountsAvailable ? (
                  <Link to="/signin" className="btn btn--ghost btn--sm">
                    Sign in
                  </Link>
                ) : null}
                <Link to="/start" className="btn btn--primary btn--sm">
                  Start learning
                </Link>
              </>
            ) : null}
          </div>
        </div>
      </header>

      <main id="main" className="site-main">
        {/* Celebrations are mounted here rather than in main.tsx so that any
            route — and any test rendering `App` — has somewhere to toast. */}
        <RewardsProvider>
          <Outlet />
        </RewardsProvider>
      </main>

      <footer className="site-footer">
        <div className="site-footer__inner">
          <div>
            <p className="site-footer__brand">{BRAND.name}</p>
            <p className="site-footer__tagline">{BRAND.tagline}</p>
          </div>
          <nav aria-label="Footer">
            <Link to="/courses">Courses</Link>
            <Link to="/map">Math Map</Link>
            {FEATURES.payments ? <Link to="/pricing">Pricing</Link> : null}
            <Link to="/about">About</Link>
            <Link to="/privacy">Privacy</Link>
          </nav>
          <p className="site-footer__note">
            Built for learners, not for advertisers. No advertising, no tracking between sites, and nothing
            collected about a child beyond their own work.
          </p>
        </div>
      </footer>

      {/*
        The same destinations as the header, shown only on a phone. It is
        `aria-hidden` when the header nav is visible would be wrong — CSS, not
        markup, decides which one is on screen — so both are labelled and a
        screen reader user simply finds the links twice, in a sensible order.
      */}
      <nav className="bottom-nav" aria-label="Sections">
        <div className="bottom-nav__inner">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to}>
              <span className="bottom-nav__icon" aria-hidden="true">
                {item.icon}
              </span>
              <span className="bottom-nav__label">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

function streakTitle(days: number, status: ReturnType<typeof streakStatus>, rest: number): string {
  const run = `${days}-day streak`;
  const cover = rest > 0 ? ` · ${rest} rest ${rest === 1 ? 'day' : 'days'} banked` : '';
  switch (status) {
    case 'at-risk':
      return `${run} · practise today to keep it${cover}`;
    case 'rescuable':
      return `${run} · a rest day is covering yesterday${cover}`;
    case 'broken':
      return 'Start a new streak today';
    default:
      return run + cover;
  }
}
